/**
 * Orders HUD Theme
 *
 * Tokens for the orders board, read off the current ground via useHUDTheme().
 *
 * Usage:
 *   const { hud, threats, badges, glass } = useHUDTheme();
 */

import { useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { useThemeMode } from '../../context/ThemeModeContext';
import { DEFAULT_GROUND, GroundTokens, defaultGroundForMode, getGround } from '../../theme/grounds';

// ============================================================================
// Types
// ============================================================================

export type HUDColorTokens = ReturnType<typeof hudColorsForGround>;
export type ThreatLevel = 'critical' | 'warning' | 'watch' | 'clear';
export type PriorityBadge = 'high-value' | 'generous-tipper' | 'repeat-customer' | 'loyalty-member' | 'first-order';

// ============================================================================
// Palette — a view over the ground (see ../../theme/grounds.ts)
// ============================================================================

/**
 * The HUD's token names predate the grounds and are kept so the board reads
 * `hud.elevated` etc. as before; the values now come from whichever ground
 * the admin is sitting on. The "accent" is the ground's crit tone (the board
 * uses it for critical threat states), NOT the ember.
 */
export function hudColorsForGround(g: GroundTokens) {
  const dark = g.mode === 'dark';
  const soft = dark ? 0.15 : 0.06;
  const glow = dark ? 0.3 : 0.12;
  const info = dark ? '#5FA8FF' : '#1D6FD1';
  const gold = dark ? '#F3C64A' : '#8A6A0A';
  return {
    // Backgrounds
    background: g.canvas,
    elevated: g.panel,
    surface: g.raised,
    hover: g.hover,

    // Brand accent (critical)
    accent: g.crit,
    accentGlow: alpha(g.crit, glow),
    accentSoft: alpha(g.crit, soft),

    // Semantic colors
    success: g.ok,
    successGlow: alpha(g.ok, glow),
    successSoft: alpha(g.ok, soft),

    warning: g.warn,
    warningGlow: alpha(g.warn, glow),
    warningSoft: alpha(g.warn, soft),

    info,
    infoGlow: alpha(info, glow),
    infoSoft: alpha(info, soft),

    critical: g.crit,
    criticalGlow: alpha(g.crit, dark ? 0.4 : 0.15),

    // Text colors
    textPrimary: g.tx,
    textSecondary: g.tx2,
    textMuted: g.tx3,

    // Borders
    border: g.line,
    borderLight: g.lineSoft,

    // VIP/Priority colors
    gold,
    goldGlow: alpha(gold, glow),
    goldSoft: alpha(gold, soft),
  };
}

const darkHudColors = hudColorsForGround(getGround(DEFAULT_GROUND));
const lightHudColors: typeof darkHudColors = hudColorsForGround(getGround(defaultGroundForMode('light')));

// ============================================================================
// Mode Selector (the default ground of that mode) — prefer useHUDTheme()
// ============================================================================

export function getHudColors(mode: 'light' | 'dark') {
  return mode === 'light' ? lightHudColors : darkHudColors;
}

function buildThreatColors(hud: HUDColorTokens): Record<ThreatLevel, { bg: string; border: string; text: string; glow: string }> {
  return {
    critical: { bg: hud.accentSoft, border: hud.accent, text: hud.accent, glow: hud.criticalGlow },
    warning: { bg: hud.warningSoft, border: hud.warning, text: hud.warning, glow: hud.warningGlow },
    watch: { bg: hud.infoSoft, border: hud.info, text: hud.info, glow: hud.infoGlow },
    clear: { bg: hud.successSoft, border: hud.success, text: hud.success, glow: hud.successGlow },
  };
}

function buildPriorityBadges(hud: HUDColorTokens): Record<PriorityBadge, { icon: string; label: string; color: string }> {
  return {
    'high-value': { icon: '💰', label: 'High Value', color: hud.gold },
    'generous-tipper': { icon: '⭐', label: 'Great Tipper', color: hud.gold },
    'repeat-customer': { icon: '🔄', label: 'Repeat', color: hud.info },
    'loyalty-member': { icon: '👑', label: 'Loyalty', color: hud.gold },
    'first-order': { icon: '🆕', label: 'First Order', color: hud.success },
  };
}

function buildGlassEffect(hud: HUDColorTokens) {
  return {
    background: alpha(hud.elevated, 0.86),
    backdropFilter: 'blur(12px)',
    border: `1px solid ${hud.borderLight}`,
  };
}

// ============================================================================
// Hook: useHUDTheme() — mode-aware (preferred)
// ============================================================================

export function useHUDTheme() {
  const { mode, ground } = useThemeMode();
  return useMemo(() => {
    const hud = hudColorsForGround(ground);
    return {
      mode,
      hud,
      threats: buildThreatColors(hud),
      badges: buildPriorityBadges(hud),
      glass: buildGlassEffect(hud),
    };
  }, [mode, ground]);
}

// ============================================================================
// Static Exports (backward-compat — dark mode locked)
// ============================================================================

/** @deprecated Use useHUDTheme() for mode-aware colors */
export const hudColors = darkHudColors;

/** @deprecated Use useHUDTheme() for mode-aware colors */
export const threatColors = buildThreatColors(darkHudColors);

/** @deprecated Use useHUDTheme() for mode-aware colors */
export const priorityBadges = buildPriorityBadges(darkHudColors);

/** @deprecated Use useHUDTheme() for mode-aware colors */
export const glassEffect = buildGlassEffect(darkHudColors);

// Capsule dimensions for virtualization (mode-independent)
export const CAPSULE_HEIGHT = 96;
export const CAPSULE_GAP = 6;

// Animation keyframes (mode-independent)
export const animations = {
  pulse: `
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `,
  glow: `
    @keyframes glow {
      0%, 100% { box-shadow: 0 0 5px currentColor; }
      50% { box-shadow: 0 0 20px currentColor, 0 0 30px currentColor; }
    }
  `,
  waveBar: `
    @keyframes waveBar {
      0%, 100% { transform: scaleY(0.3); }
      50% { transform: scaleY(1); }
    }
  `,
};

export default hudColors;
